
  import { computed, defineComponent, onMounted, ref, watch } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRoute, useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import { reinitializeComponents } from '@/core/plugins/keenthemes';
  import PropertiesCreating from '@/views/manage/auctions/properties/PropertiesCreating.vue';
  import PropertiesListing from '@/views/manage/auctions/properties/PropertiesListing.vue';
  import PropertiesEditing from '@/views/manage/auctions/properties/PropertiesEditing.vue';
  import { Auction } from '@/store/modules/Auctions/AuctionsModule';
  import Lightgallery from 'lightgallery/vue';
  import lgZoom from 'lightgallery/plugins/zoom';
  import lgVideo from 'lightgallery/plugins/video';
  let lightGallery;

  export default defineComponent({
    name: 'auctions-editing',
    components: {
      ErrorMessage,
      Field,
      Form,
      PropertiesCreating,
      PropertiesListing,
      PropertiesEditing,
      Lightgallery,
    },
    data: () => ({
      plugins: [lgZoom, lgVideo],
    }),
    async setup() {
      const { t, te } = useI18n();
      const { can } = useAbility();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const submitVideoButton = ref<HTMLButtonElement | null>(null);
      const loading = ref(false);
      const editData = ref<number>(0);
      const selectedOrganizer = ref<number>(0);
      const selectedLocation = ref<number>(0);
      const videoInput = ref('');
      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        store.dispatch(Actions.ADD_BODY_CLASSNAME, 'page-loading-over');

        router.back();
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('auctionsEditing'), [
          translate('auctions'),
        ]);
        reinitializeComponents();
      });

      watch(
        () => selectedOrganizer.value,
        async () => {
          if (selectedOrganizer.value) {
            selectedLocation.value = 0;
            await store.dispatch(
              Actions.GET_ORGANIZER_LOCATIONS,
              selectedOrganizer.value
            );
          }
        }
      );
      watch(videoInput, (newVideoInput, oldVideoInput) => {
        if (!newVideoInput) {
          videoInput.value = oldVideoInput;
        }
      });
      const auctionsSchema = Yup.object().shape({
        organiserId: Yup.number()
          .notOneOf([0], () => translate('ORGANIZER_IS_REQUIRED_FIELD'))
          .required(() => translate('ORGANIZER_IS_REQUIRED_FIELD'))
          .label('Organizer'),
        organisersLocationsId: Yup.number()
          .notOneOf([0], () =>
            translate('ORGANIZER_LOCATION__IS_REQUIRED_FIELD')
          )
          .required(() => translate('ORGANIZER_LOCATION__IS_REQUIRED_FIELD'))
          .label('Organizer Location'),
        eventDatetime: Yup.date()
          .required(() => translate('EVENT_DATE_TIME_IS_REQUIRED_FIELD'))
          .label('Date and Time'),
      });

      const { data } = await store.dispatch(
        Actions.GET_AUCTION,
        route.params.id
      );

      const auction = ref<Auction>(data.data);

      const guideVideo = ref(auction.value.guideVideo);

      const date = new Date(auction.value.eventDateTime);
      const expiryBannerDate = new Date(auction.value.expiryBanner);

      auction.value.eventDateTime = new Date(date).toISOString().slice(0, -1);
      auction.value.expiryBanner = new Date(
        expiryBannerDate.getTime() - date.getTimezoneOffset() * 60000
      )
        .toISOString()
        .slice(0, -1);
      const onInit = (detail) => {
        lightGallery = detail.instance;
      };
      const updateSlides = () => {
        lightGallery.refresh();
      };
      const previewVideo = (e) => {
        const file = e.target.files[0];
        if (file) {
          guideVideo.value = URL.createObjectURL(file);
          updateSlides();
        }
      };

      const onSubmitUpdate = async (values) => {
        if (values.certifiedCheckRequest == null)
          values.certifiedCheckRequest = false;
        if (values.certifiedCheckRequest == false)
          values.certifiedCheckRequest = false;
        else values.certifiedCheckRequest = true;

        if (values.loanRequest == null) values.loanRequest = false;
        if (values.loanRequest == false) values.loanRequest = false;
        else values.loanRequest = true;
        if (submitButton.value) {
          // eslint-disable-next-line
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        debugger;
        values.eventDatetime = new Date(
          new Date(values.eventDatetime).getTime() + 3 * 60 * 60 * 1000
        ).toISOString();

        await store.dispatch(Actions.UPDATE_AUCTION, {
          data: values,
          id: auction.value.id,
        });
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_AUCTION'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            //Deactivate indicator
            submitButton.value?.removeAttribute('data-kt-indicator');
            // eslint-disable-next-line
            submitButton.value!.disabled = false;
          });
        }
      };

      const onSubmitVideo = async () => {
        if (submitVideoButton.value) {
          // eslint-disable-next-line
          submitVideoButton.value!.disabled = true;
          submitVideoButton.value.setAttribute('data-kt-indicator', 'on');
        }

        if (!videoInput.value) {
          return;
        }

        const videoFormData = new FormData();
        // eslint-disable-next-line
        videoFormData.append('video', videoInput.value);

        await store.dispatch(Actions.UPLOAD_AUCTION_VIDEO, {
          data: videoFormData,
          auctionId: auction.value?.id,
        });
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitVideoButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitVideoButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPLOADED_AUCTION_VIDEO'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.go(0);
            //Deactivate indicator
            submitVideoButton.value?.removeAttribute('data-kt-indicator');
          });
        }
      };

      await store.dispatch(Actions.GET_ORGANIZERS, { limit: 100 });
      await store.dispatch(
        Actions.GET_ORGANIZER_LOCATIONS,
        auction.value.organiserId
      );

      const refreshProperties = async () => {
        await store.dispatch(Actions.GET_AUCTION_PROPERTIES, auction.value.id);
      };

      const showPropertyEditModal = (val) => {
        editData.value = val;
      };

      await refreshProperties();
      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        auction,
        editData,
        showPropertyEditModal,
        submitVideoButton,
        onSubmitVideo,
        onSubmitUpdate,
        submitButton,
        translate,
        auctionsSchema,
        goBack,
        can,
        loading,
        organizers: computed(() => store.getters.organizersList),
        locations: computed(() => store.getters.organizerLocationsList),
        selectedOrganizer,
        selectedLocation,
        videoInput,
        refreshProperties,
        guideVideo,
        previewVideo,
        onInit,
        updateSlides,
      };
    },
  });
